import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Copy, Trash2, X } from 'lucide-react';

import Input from '../../ui/Input';
import Button from '../../ui/Button';
import ProductPickerModal from './ProductPickerModal';

// Error correction levels, roughly how much of the code can be obscured and
// still scan. Higher = denser code but more robust. Same labels as
// vericyte/ui's QrCodeWizard (see blocks/qr-block.md "Editable properties").
const EC_LEVELS = [
  { value: 'L', label: 'Low' },
  { value: 'M', label: 'Med-Low' },
  { value: 'Q', label: 'Med-High' },
  { value: 'H', label: 'High' },
];

// Type-specific panel for a selected QR block. Position/size live in the
// generic PositionPanel/SizePanel. QR-specific properties are the raw
// string it encodes (a manually-typed URL/text for now; see
// blocks/qr-block.md "Composes with" for the deferred, optional
// coupon-sourced value, which would still just be this same `value` field)
// plus the code's own appearance (error correction, colors, quiet-zone
// margin), matched to qrcode.react's QRCodeSVG props.
export default function QRPropertiesPanel({ block, onUpdateProperty, onDelete }) {
  const [pickerOpen, setPickerOpen] = useState(false);

  if (!block) return null;
  const collapsed = block.collapsed;

  return (
    <div className="ss-print__panel">
      <div className="ss-print__panel-header">
        <h3 className="ss-print__panel-title">QR Properties</h3>
        <div className="ss-print__panel-header-actions">
          <button
            type="button"
            className="ss-print__lock-toggle ss-print__lock-toggle--danger"
            onClick={onDelete}
            aria-label="Delete this QR block"
            title="Delete"
          >
            <Trash2 size={16} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="ss-print__lock-toggle"
            onClick={() => onUpdateProperty('collapsed', !collapsed)}
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Expand QR properties panel' : 'Collapse QR properties panel'}
            title={collapsed ? 'Expand' : 'Collapse'}
          >
            {collapsed ? <ChevronRight size={16} aria-hidden="true" /> : <ChevronDown size={16} aria-hidden="true" />}
          </button>
        </div>
      </div>
      {!collapsed && (
        <>
          <div className="ss-field">
            <label className="ss-label" htmlFor="ss-print-qr-value">
              Value (URL or text)
            </label>
            <div className="ss-print__panel-row" style={{ alignItems: 'center' }}>
              <input
                id="ss-print-qr-value"
                className="ss-input"
                type="text"
                value={block.value}
                onChange={(e) => onUpdateProperty('value', e.target.value)}
              />
              <button
                type="button"
                className="ss-print__lock-toggle"
                onClick={() => onUpdateProperty('value', '')}
                aria-label="Clear QR value"
                title="Clear value"
              >
                <X size={16} aria-hidden="true" />
              </button>
            </div>
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={() => setPickerOpen(true)}>
            <Copy size={16} aria-hidden="true" /> Copy product link…
          </Button>
          <ProductPickerModal isOpen={pickerOpen} onClose={() => setPickerOpen(false)} />
          <div className="ss-print__panel-row">
            <div className="ss-field">
              <label className="ss-label" htmlFor="ss-print-qr-ec-level">
                Reliability
              </label>
              <select
                id="ss-print-qr-ec-level"
                className="ss-input"
                value={block.ecLevel}
                onChange={(e) => onUpdateProperty('ecLevel', e.target.value)}
              >
                {EC_LEVELS.map((lvl) => (
                  <option key={lvl.value} value={lvl.value}>
                    {lvl.label}
                  </option>
                ))}
              </select>
            </div>
            <Input
              label="Edge Space"
              type="number"
              min={0}
              max={8}
              value={block.marginModules}
              onChange={(e) => onUpdateProperty('marginModules', Number(e.target.value))}
            />
          </div>
          <div className="ss-print__panel-row">
            <div className="ss-field">
              <label className="ss-label" htmlFor="ss-print-qr-fg-color">
                Foreground
              </label>
              <input
                id="ss-print-qr-fg-color"
                type="color"
                className="ss-input"
                value={block.fgColor}
                onChange={(e) => onUpdateProperty('fgColor', e.target.value)}
              />
            </div>
            <div className="ss-field">
              <label className="ss-label" htmlFor="ss-print-qr-bg-color">
                Background
              </label>
              <input
                id="ss-print-qr-bg-color"
                type="color"
                className="ss-input"
                value={block.bgColor}
                onChange={(e) => onUpdateProperty('bgColor', e.target.value)}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
